import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdEntityListGroupDto,
  InternalNetworkAdEntityKind,
} from '@terra/shared-contracts-internal-network';
import { terraProxyApiClient } from '../../../../clients/testerClient';
import { SURFACE_ASSETS_KEY } from '../../../../hooks/api/useSurfaceAssets';
import {
  INTERNAL_ASSETS_PAGE_SIZE,
  toInternalAdOrderBy,
  toInternalAssetsSearchTerm,
  toInternalCanonicalOrderBy,
  type InternalAssetGridItem,
  type InternalAssetsGroupByField,
  type InternalAssetsListMode,
  type InternalAssetsSort,
} from './internal-assets-table.model';

const NO_GROUPS: readonly AdEntityListGroupDto[] = [];

const dedupeByRowId = (
  items: readonly InternalAssetGridItem[],
): InternalAssetGridItem[] => [
  ...new Map(items.map((item) => [item.rowId, item])).values(),
];

export function useInternalAssetsListData({
  networkId,
  mode,
  entityKind,
  search,
  sort,
  groupBy,
}: {
  networkId: string;
  mode: InternalAssetsListMode;
  entityKind: InternalNetworkAdEntityKind | null;
  search: string;
  sort: InternalAssetsSort;
  groupBy: InternalAssetsGroupByField | undefined;
}) {
  const queryClient = useQueryClient();
  const term = toInternalAssetsSearchTerm(search);
  const adOrderBy = toInternalAdOrderBy(sort);
  const adGroupBy = groupBy === 'assetKind' ? undefined : groupBy;
  const canonicalOrderBy = toInternalCanonicalOrderBy(sort);
  const canonicalGroupBy = groupBy === 'assetKind' ? groupBy : undefined;

  const adQuery = useInfiniteQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      'internal-ad-pages',
      networkId,
      entityKind,
      term,
      adOrderBy,
      adGroupBy,
    ],
    queryFn: ({ pageParam }) => {
      const page = {
        networkId,
        entityKind: entityKind ?? undefined,
        skip: pageParam,
        limit: INTERNAL_ASSETS_PAGE_SIZE,
        orderBy: adOrderBy,
        groupBy: adGroupBy,
      };

      return term
        ? terraProxyApiClient.searchInternalNetworkAdEntities({
            ...page,
            term,
          })
        : terraProxyApiClient.listInternalNetworkAdEntities(page);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.entities.length, 0);

      return lastPage.entities.length === INTERNAL_ASSETS_PAGE_SIZE &&
        loaded < lastPage.total
        ? loaded
        : undefined;
    },
    enabled: Boolean(networkId) && mode === 'ad',
  });

  const canonicalQuery = useInfiniteQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      'internal-canonical-pages',
      networkId,
      term,
      canonicalOrderBy,
      canonicalGroupBy,
    ],
    queryFn: ({ pageParam }) => {
      const query = {
        limit: INTERNAL_ASSETS_PAGE_SIZE,
        cursor: pageParam,
        orderBy: canonicalOrderBy,
        groupBy: canonicalGroupBy,
      };

      return term
        ? terraProxyApiClient.searchCanonicalInternalNetworkAssets({
            networkId,
            query: { ...query, search: term },
          })
        : terraProxyApiClient.listCanonicalInternalNetworkAssets({
            networkId,
            query,
          });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(networkId) && mode === 'canonical',
  });

  const adPages = adQuery.data?.pages;
  const canonicalPages = canonicalQuery.data?.pages;
  const items = useMemo((): InternalAssetGridItem[] => {
    if (mode === 'ad') {
      return dedupeByRowId(
        (adPages ?? []).flatMap((page) =>
          page.entities.map(
            (entity): InternalAssetGridItem => ({
              source: 'ad',
              rowId: `ad:${entity.nodeId}`,
              label: entity.displayName,
              entity,
            }),
          ),
        ),
      );
    }

    return dedupeByRowId(
      (canonicalPages ?? []).flatMap((page) =>
        page.items.map(
          (asset): InternalAssetGridItem => ({
            source: 'canonical',
            rowId: `asset:${asset.id}`,
            label: asset.displayName,
            asset,
          }),
        ),
      ),
    );
  }, [adPages, canonicalPages, mode]);
  const activeQuery = mode === 'ad' ? adQuery : canonicalQuery;
  const firstAdPage = adPages?.[0];
  const firstCanonicalPage = canonicalPages?.[0];
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [SURFACE_ASSETS_KEY] });
  }, [queryClient]);
  const fetchNextAdPage = adQuery.fetchNextPage;
  const fetchNextCanonicalPage = canonicalQuery.fetchNextPage;
  const loadMore = useCallback(() => {
    void (mode === 'ad' ? fetchNextAdPage() : fetchNextCanonicalPage());
  }, [fetchNextAdPage, fetchNextCanonicalPage, mode]);

  return {
    items,
    groups:
      (mode === 'ad' ? firstAdPage?.groups : firstCanonicalPage?.groups) ??
      NO_GROUPS,
    total:
      (mode === 'ad' ? firstAdPage?.total : firstCanonicalPage?.total) ?? 0,
    sortApplied: mode === 'canonical' || firstAdPage?.sortApplied !== false,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    isFetchingMore: activeQuery.isFetchingNextPage,
    hasMore: Boolean(activeQuery.hasNextPage),
    loadMore,
    refresh,
  };
}
